import {
    RefObject,
    useCallback,
    useEffect,
    useEffectEvent,
    useLayoutEffect,
    useRef,
    useState,
} from "react";
import type { OnChangeFn, RowData, Updater } from "@tanstack/react-table";
import type { ViewTableReactInstance } from "./table";
import type { View } from "@common/types/views/views";
import { getLeafColumnHeaders } from "./getLeafColumnHeaders";
import { isFillerColumn } from "./fillerColumns";

/**
 * Owns all column-resize state and side effects for a ViewTable instance:
 * - Maintains local `columnSizing` / `userResizedColumnIds`.
 * - Measures initial widths from the rendered DOM (so non-resized columns
 *   start aligned with their `minmax(min, 1fr)` natural size).
 * - Persists user resizes back to `view.options.size` via `onViewChange`,
 *   with a configurable debounce (`viewChangeDebounceMs === 0` runs the
 *   commit synchronously for shared-view / sibling-alignment cases).
 * - Clears user-resize tracking when a view comes in without the size,
 *   so resets "stick" instead of re-inflating from stale local state.
 *
 * The caller supplies `tableRef` (populated right after `useTable`)
 * and `tableContainerRef` (for DOM width reads). Returned `columnSizing`
 * and `onColumnSizingChange` are wired back into `useTable`'s state.
 */
interface UseColumnResizeSyncArgs<TData extends RowData> {
    tableRef: RefObject<ViewTableReactInstance<TData>>;
    tableContainerRef: RefObject<HTMLDivElement | null>;
    view: View;
    onViewChange?: (view: View) => void;
    viewChangeDebounceMs: number;
    enableColumnResizing: boolean;
    columnsLength: number;
}

interface UseColumnResizeSyncResult {
    columnSizing: Record<string, number>;
    onColumnSizingChange: OnChangeFn<Record<string, number>>;
    userResizedColumnIds: ReadonlySet<string>;
}

interface SizingState {
    columnSizing: Record<string, number>;
    // Columns the user has explicitly resized in this ViewTable instance.
    // Read by `persistView` to know which sizes to write back to the view,
    // by `computeColumnSizingFromDOM` to avoid overwriting user widths, and
    // by the grid template. Changes only together with `columnSizing`.
    userResizedColumnIds: ReadonlySet<string>;
}

const INITIAL_SIZING_STATE: SizingState = {
    columnSizing: {},
    userResizedColumnIds: new Set(),
};

export function useColumnResizeSync<TData extends RowData>({
    tableRef,
    tableContainerRef,
    view,
    onViewChange,
    viewChangeDebounceMs,
    enableColumnResizing,
    columnsLength,
}: UseColumnResizeSyncArgs<TData>): UseColumnResizeSyncResult {
    const [sizingState, setSizingState] = useState(INITIAL_SIZING_STATE);
    const { columnSizing, userResizedColumnIds } = sizingState;
    const viewRef = useRef(view);

    useEffect(() => {
        viewRef.current = view;
    }, [view]);

    const onColumnSizingChange = useCallback<
        OnChangeFn<Record<string, number>>
    >((updater: Updater<Record<string, number>>) => {
        setSizingState((prev) => {
            const next =
                typeof updater === "function"
                    ? updater(prev.columnSizing)
                    : updater;
            const newlyResizedColumnIds = Object.keys(next).filter(
                (key) =>
                    prev.columnSizing[key] !== next[key] &&
                    !prev.userResizedColumnIds.has(key),
            );
            return {
                columnSizing: next,
                userResizedColumnIds:
                    newlyResizedColumnIds.length > 0
                        ? new Set([
                              ...prev.userResizedColumnIds,
                              ...newlyResizedColumnIds,
                          ])
                        : prev.userResizedColumnIds,
            };
        });
    }, []);

    // Seed `columnSizing` from the rendered DOM widths for columns the user
    // hasn't touched. Without this, the initial `minmax(min, 1fr)` track
    // sizes wouldn't be reflected back into TanStack's size state, so pinned
    // column offsets and `column.getSize()` consumers would read 0/default.
    const computeColumnSizingFromDOM = useCallback(() => {
        if (!enableColumnResizing) {
            return;
        }

        const container = tableContainerRef.current;
        if (!container) {
            return;
        }

        const headerElements = container.querySelectorAll("[data-header-cell]");
        if (headerElements.length === 0) {
            return;
        }

        const headers = getLeafColumnHeaders(tableRef.current);
        const measuredSizes: Array<{
            headerId: string;
            columnId: string;
            width: number;
        }> = [];

        headers.forEach((header, index) => {
            const columnDef = header.column.columnDef;
            const columnId = columnDef.id;
            // Skip filler and select; user-resized columns are skipped below
            if (
                !columnId ||
                isFillerColumn(columnId) ||
                columnId === "select"
            ) {
                return;
            }
            const headerEl = headerElements[index] as HTMLElement;
            if (headerEl) {
                measuredSizes.push({
                    headerId: header.id,
                    columnId,
                    width: headerEl.offsetWidth,
                });
            }
        });

        if (measuredSizes.length > 0) {
            setSizingState((prev) => {
                const newSizing = Object.fromEntries(
                    measuredSizes
                        .filter(
                            ({ columnId }) =>
                                !prev.userResizedColumnIds.has(columnId),
                        )
                        .map(({ headerId, width }) => [headerId, width]),
                );
                const hasChanges = Object.entries(newSizing).some(
                    ([key, val]) => prev.columnSizing[key] !== val,
                );
                return hasChanges
                    ? {
                          ...prev,
                          columnSizing: { ...prev.columnSizing, ...newSizing },
                      }
                    : prev;
            });
        }
    }, [enableColumnResizing, tableContainerRef, tableRef]);

    const seedColumnSizingOnMount = useEffectEvent(computeColumnSizingFromDOM);
    useLayoutEffect(() => {
        seedColumnSizingOnMount();
    }, []);

    const prevColumnsLengthRef = useRef(columnsLength);
    useLayoutEffect(() => {
        if (columnsLength !== prevColumnsLengthRef.current) {
            prevColumnsLengthRef.current = columnsLength;
            computeColumnSizingFromDOM();
        }
    }, [columnsLength, computeColumnSizingFromDOM]);

    // Re-seed column widths whenever the scroll container resizes (window
    // resize, task / chat / details panel toggle, page splitter drag, etc.).
    //
    // Without this, CSS grid `minmax(min, max)` tracks can re-flow to a width
    // different from what's stored in TanStack's `columnSizing` state. When
    // that happens, `column.getAfter("end")` feeds the wrong sticky `right:`
    // offset into pinned-right columns, leaving a transparent gap (or overlap)
    // between adjacent pinned columns through which the scrolling content
    // underneath becomes visible. See PRO-9320.
    //
    // rAF coalesces bursty resize callbacks; the `isResizingColumn` guard
    // avoids fighting with an in-flight column drag, which already manages
    // its own width via `onColumnSizingChange`.
    useLayoutEffect(() => {
        const container = tableContainerRef.current;
        if (!container || !enableColumnResizing) {
            return;
        }

        let rafId: number | null = null;
        const observer = new ResizeObserver(() => {
            if (rafId !== null) {
                cancelAnimationFrame(rafId);
            }
            rafId = requestAnimationFrame(() => {
                rafId = null;
                const isUserResizingColumn =
                    !!tableRef.current.state.columnResizing.isResizingColumn;
                if (isUserResizingColumn) {
                    return;
                }
                computeColumnSizingFromDOM();
            });
        });
        observer.observe(container);

        return () => {
            observer.disconnect();
            if (rafId !== null) {
                cancelAnimationFrame(rafId);
            }
        };
    }, [
        enableColumnResizing,
        tableContainerRef,
        tableRef,
        computeColumnSizingFromDOM,
    ]);

    // When a column loses its `options.size` in the incoming view (e.g. a
    // "reset sizes" action), forget any local user-resize tracking for it so
    // `gridTemplateColumns` falls back to `minmax(min, 1fr)` instead of
    // re-emitting the stale local size.
    const forgetResizesDroppedFromView = useEffectEvent((nextView: View) => {
        const columnsToReset = Array.from(userResizedColumnIds).filter(
            (columnId) => {
                const column = nextView.columns.find((c) => c.id === columnId);
                return !column?.options?.size;
            },
        );

        if (columnsToReset.length > 0) {
            setSizingState((prev) => {
                const nextSizing = { ...prev.columnSizing };
                columnsToReset.forEach((columnId) => {
                    delete nextSizing[columnId];
                });
                return {
                    columnSizing: nextSizing,
                    userResizedColumnIds: new Set(
                        Array.from(prev.userResizedColumnIds).filter(
                            (columnId) => !columnsToReset.includes(columnId),
                        ),
                    ),
                };
            });

            requestAnimationFrame(() => {
                computeColumnSizingFromDOM();
            });
        }
    });
    useEffect(() => {
        forgetResizesDroppedFromView(view);
    }, [view]);

    const persistView = useCallback(() => {
        if (
            !enableColumnResizing ||
            !onViewChange ||
            userResizedColumnIds.size === 0
        ) {
            return;
        }

        const currentView = viewRef.current;
        let hasChanges = false;
        const updatedColumns = currentView.columns.map((column) => {
            const columnId = column.id;
            // Only persist columns the user has actually resized
            if (!userResizedColumnIds.has(columnId)) {
                return column;
            }
            // Prefer the column's clamped size (honors minSize/maxSize from
            // columnDef) over the raw columnSizing state. TanStack stores the
            // un-clamped drag value in columnSizing; if we persisted that
            // directly, sibling ViewTables (which render from `view.options.
            // size`) would keep growing past the dragged table's ceiling.
            const newSize =
                tableRef.current.getColumn(columnId)?.getSize() ??
                columnSizing[columnId as keyof typeof columnSizing];

            // No resolvable size (e.g. column not registered with the table
            // and no columnSizing entry) — leave the view column untouched
            // rather than writing `undefined`.
            if (newSize === undefined) {
                return column;
            }
            // Already up to date — no view update needed.
            if (newSize === column.options?.size) {
                return column;
            }
            hasChanges = true;
            return {
                ...column,
                options: {
                    ...column.options,
                    size: newSize,
                },
            };
        });

        // Skip the view update if the persisted sizes are already current.
        // With viewChangeDebounceMs=0 this avoids a render storm while the
        // user drags past maxSize (raw columnSizing keeps changing, but the
        // clamped size stays at the ceiling).
        if (!hasChanges) {
            return;
        }

        onViewChange({
            ...currentView,
            columns: updatedColumns,
        });
    }, [
        columnSizing,
        enableColumnResizing,
        onViewChange,
        tableRef,
        userResizedColumnIds,
    ]);

    // Persist resizes back to `view`. `viewChangeDebounceMs === 0` is a
    // deliberate opt-in (used when several ViewTable instances share one
    // view and need to stay column-aligned in real time) — in that case we
    // skip the setTimeout hop and run persistView right after the drag
    // render commits, so sibling tables follow without a macrotask delay.
    useEffect(() => {
        if (viewChangeDebounceMs === 0) {
            persistView();
            return;
        }
        const handler = setTimeout(persistView, viewChangeDebounceMs);
        return () => {
            clearTimeout(handler);
        };
    }, [persistView, viewChangeDebounceMs, columnSizing]);

    return {
        columnSizing,
        onColumnSizingChange,
        userResizedColumnIds,
    };
}
